import { useState, useEffect, useCallback, Fragment } from "react";
import { API } from "../config";

const FONT = "'DM Sans', sans-serif";

const CARD = {
  background: "#fff",
  borderRadius: 12,
  padding: "20px 24px",
  marginBottom: 16,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  border: "1px solid #E2E8F0",
};

const H2 = {
  fontSize: 15,
  fontWeight: 600,
  color: "#1E293B",
  marginBottom: 12,
  letterSpacing: "-0.01em",
};

const H3 = {
  fontSize: 14,
  fontWeight: 600,
  color: "#334155",
  margin: "18px 0 6px 0",
};

const P = {
  fontSize: 13,
  lineHeight: 1.7,
  color: "#475569",
  margin: "0 0 10px 0",
};

const NOTA = {
  background: "#F8FAFC",
  borderLeft: "3px solid #94A3B8",
  borderRadius: 0,
  padding: "12px 14px",
  marginTop: 12,
  fontSize: 13,
  lineHeight: 1.7,
  color: "#334155",
};

const TAB = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const TH = {
  padding: "6px 8px 8px 8px",
  borderBottom: "2px solid #CBD5E1",
  fontWeight: 700,
  color: "#1E293B",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const TD = {
  padding: "7px 8px",
  borderBottom: "1px solid #F1F5F9",
  color: "#334155",
  verticalAlign: "top",
};

function Tabella({ intestazioni, righe, larghezze }) {
  return (
    <table style={{ ...TAB, tableLayout: "fixed" }}>
      <colgroup>
        {larghezze.map((w, i) => (
          <col key={i} style={{ width: w }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {intestazioni.map((h, i) => (
            <th key={i} style={TH}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {righe.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td
                key={j}
                style={{
                  ...TD,
                  borderBottom: i === righe.length - 1 ? "none" : TD.borderBottom,
                  color: c === "—" ? "#CBD5E1" : TD.color,
                  whiteSpace: String(c).length <= 20 ? "nowrap" : "normal",
                }}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const SEZ_BTN = {
  fontFamily: FONT,
  fontSize: 13,
  fontWeight: 600,
  padding: "9px 16px",
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  background: "#fff",
  color: "#475569",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
};

const SEZ_BTN_ON = {
  ...SEZ_BTN,
  background: "#2563EB",
  border: "1px solid #2563EB",
  color: "#fff",
};

export default function AnagraficaStorico() {
  const [sezione, setSezione] = useState("descrizione");

  return (
    <div style={{ padding: "24px 32px", fontFamily: FONT, background: "#FAFBFC", minHeight: "100vh" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1E293B", marginBottom: 4, letterSpacing: "-0.02em" }}>
        📚 Anagrafica del Database storico
      </h1>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>
        Che cosa c'è nel database storico, e da quando.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <button
          style={sezione === "descrizione" ? SEZ_BTN_ON : SEZ_BTN}
          onClick={() => setSezione("descrizione")}
        >
          📄 Descrizione
        </button>
        <button
          style={sezione === "copertura" ? SEZ_BTN_ON : SEZ_BTN}
          onClick={() => setSezione("copertura")}
        >
          📊 Copertura delle quote
        </button>
      </div>

      {sezione === "copertura" && <CoperturaQuote />}

      {sezione === "descrizione" && (
      <>
      <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", marginBottom: 24, maxWidth: 780 }}>
        Aggiornata al 24 agosto 2026. Cambia quando si aggiunge un campionato o un mercato — non con il passare del
        tempo.
      </p>

      <div style={{ ...CARD, maxWidth: 900 }}>
        <p style={P}>
          Il database è stato costruito in più fasi temporali: inizialmente era costituito solo dai 4 maggiori
          campionati europei con il solo mercato delle quote 1X2. Poi, nel tempo è stato ampliato con l'inserimento di
          altri campionati e di altri mercati. Chiaramente ciò impatta con l'esecuzione dei back test in fase di
          creazione di un sistema. Infatti, ad esempio, un mercato inserito nella stagione 2022/23 non può avere un back
          test antecedente a tale stagione.
        </p>
        <p style={{ ...P, margin: 0 }}>Di seguito il riepilogo della situazione:</p>
      </div>

      {/* ===== I CAMPIONATI ===== */}
      <div style={{ ...CARD, maxWidth: 900 }}>
        <div style={H2}>I campionati</div>
        <Tabella
          intestazioni={["Stagioni", "Quanti", "Quali"]}
          larghezze={["22%", "10%", "68%"]}
          righe={[
            ["dalla 12-13", "4", "English Premier League, German Bundesliga, Italia Serie A, Spagna Liga"],
            [
              "dalla 16-17",
              "11",
              "i quattro sopra + Belgio, Olanda, English Championship, Francia Ligue 1, German Bundesliga 2, Portogallo Primeira Liga, Turchia Süper Lig",
            ],
          ]}
        />
        <p style={{ ...P, marginTop: 14, marginBottom: 0 }}>
          Un sistema sulla Serie A guarda indietro fino alla 12-13 mentre uno sul Portogallo fino alla 16-17.
        </p>
      </div>

      {/* ===== I MERCATI ===== */}
      <div style={{ ...CARD, maxWidth: 900 }}>
        <div style={H2}>I mercati</div>
        <Tabella
          intestazioni={["Mercato", "ore 9", "5 min dal via", "ore 8"]}
          larghezze={["22%", "26%", "26%", "26%"]}
          righe={[
            ["1 · X · 2", "Solo punta", "punta e banca", "punta e banca"],
            ["Under/Over 1.5", "—", "punta e banca", "punta e banca"],
            ["Under/Over 2.5", "—", "punta e banca", "punta e banca"],
            ["Under/Over 3.5", "—", "punta e banca", "punta e banca"],
            ["Gol / NoGol", "—", "—", "punta e banca"],
          ]}
        />
        <p style={{ ...P, marginTop: 14, marginBottom: 0 }}>
          Le ore 9 hanno <strong style={{ color: "#1E293B" }}>il solo 1X2</strong>, e{" "}
          <strong style={{ color: "#1E293B" }}>solo il prezzo di punta</strong>. Il profitto in banca esiste lo stesso,
          ma è calcolato sulla quota di punta: è un'approssimazione, senza lo scarto fra denaro e lettera. Ne va tenuto
          conto leggendo un back test in banca sulle ore 9.
        </p>
      </div>

      {/* ===== L'EVOLUZIONE DEI MERCATI NEL TEMPO ===== */}
      <div style={{ ...CARD, maxWidth: 900 }}>
        <div style={H2}>L'evoluzione dei mercati nel tempo</div>
        <p style={P}>
          Per tutti i mercati, le quote fino alla data del 13 marzo 2026 sono state importate da Betfair.com mediante
          fornitore di servizi esterno. A partire da quella data vengono scaricate, da betfair.it, mediante automatismo
          proprietario “Investisport”.
        </p>

        <div style={H3}>Ore 9 — dalla 12-13</div>
        <p style={{ ...P, marginBottom: 0 }}>
          Ci sono da sempre, su tutti i campionati presenti in ciascuna stagione. Sono la spina dorsale dello storico.
        </p>

        <div style={H3}>5 minuti dal calcio d'inizio — dall'8 settembre 2017</div>
        <p style={{ ...P, marginBottom: 0 }}>
          Nascono a stagione 17-18 già iniziata. Prima non ci sono quote su nessun mercato.
        </p>
        <div style={NOTA}>
          <strong style={{ fontWeight: 600 }}>Conseguenza pratica:</strong> quando un sistema opera su un mercato
          5minKO, o lo usa come filtro, il back test parte dall'08/09/2017. Le partite precedenti escono dal conteggio:
          non sono un dato mancante, sono fuori periodo e non vengono contate come operazioni.
        </div>

        <div style={H3}>Ore 8 — dal 28 ottobre 2022</div>
        <p style={P}>
          Dal 28 ottobre 2022 al 12 febbraio 2026 le quote sono state importate dal fornitore esterno. Poi abbiamo un
          buco di 1 mese fino alla data del 13 marzo 2026, data dalla quale inizia il download proprietario delle quote.
        </p>
        <p style={{ ...P, marginBottom: 0 }}>Prima del 28 ottobre 2022 non ci sono quote su nessun mercato.</p>
        <div style={NOTA}>
          <strong style={{ fontWeight: 600 }}>Conseguenza pratica:</strong> quando un sistema opera su un mercato ore 8,
          o lo usa come filtro, il back test parte dal 28/10/2022. Le partite precedenti escono dal conteggio: non sono
          un dato mancante, sono fuori periodo e non vengono contate come operazioni.
        </div>

        <p style={{ ...P, marginTop: 18, marginBottom: 12 }}>In breve:</p>
        <Tabella
          intestazioni={["Dato", "Da quando", "Punta", "Banca"]}
          larghezze={["22%", "26%", "26%", "26%"]}
          righe={[
            ["Quote ore 9", "stagione 12-13", "sì", "assunta uguale alla punta"],
            ["Quote 5 min dal via (5minKO)", "8 settembre 2017 (stagione 17-18)", "sì", "sì"],
            ["Quote ore 8", "28 ottobre 2022 (stagione 22-23)", "sì", "sì"],
          ]}
        />
      </div>

      {/* ===== PROFITTI E PERDITE ===== */}
      <div style={{ ...CARD, maxWidth: 900 }}>
        <div style={H2}>Profitti e perdite</div>
        <p style={{ ...P, marginBottom: 0 }}>
          Regola unica: <strong style={{ color: "#1E293B" }}>se la quota non esiste, il profitto non esiste.</strong>
        </p>
      </div>
      </>
      )}
    </div>
  );
}


/* ===========================================================================
   COPERTURA STORICA DELLE QUOTE (v233)
   Due viste indipendenti: per mercato e per campionato.
   I filtri di una vista non toccano l'altra.
   =========================================================================== */

const BTN = {
  fontFamily: FONT,
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 6,
  border: "1px solid #CBD5E1",
  background: "#fff",
  color: "#475569",
  cursor: "pointer",
};

const TAB_BTN = {
  ...BTN,
  fontSize: 13,
  padding: "5px 14px",
};

const TAB_BTN_ON = {
  ...TAB_BTN,
  border: "1px solid #2563EB",
  color: "#2563EB",
  background: "#EFF6FF",
};

const INPUT_DATA = {
  fontFamily: FONT,
  fontSize: 12,
  height: 28,
  padding: "0 6px",
  borderRadius: 6,
  border: "1px solid #CBD5E1",
  color: "#334155",
  background: "#fff",
};

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const SELECT_STAG = {
  fontFamily: FONT,
  fontSize: 12,
  height: 28,
  padding: "0 6px",
  borderRadius: 6,
  border: "1px solid #CBD5E1",
  color: "#334155",
  background: "#fff",
  width: 190,
};

const CHIP = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  padding: "3px 8px",
  borderRadius: 6,
  border: "1px solid #2563EB",
  background: "#EFF6FF",
  color: "#2563EB",
  marginRight: 6,
  marginTop: 4,
};

const CHIP_X = {
  cursor: "pointer",
  fontWeight: 700,
  lineHeight: 1,
};

const POP = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 6px)",
  zIndex: 30,
  width: 320,
  background: "#fff",
  border: "1px solid #CBD5E1",
  borderRadius: 8,
  boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
  padding: "10px 12px",
  fontSize: 12,
  lineHeight: 1.6,
  color: "#334155",
  textAlign: "left",
  whiteSpace: "normal",
  fontWeight: 400,
};

function dataEstesa(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  if (p.length !== 3) return iso;
  return `${parseInt(p[2], 10)} ${MESI[parseInt(p[1], 10) - 1]} ${p[0]}`;
}

function dataBreve(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  if (p.length !== 3) return iso;
  return `${p[2]}/${p[1]}`;
}

function numero(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("it-IT");
}

const TH_FISSO = {
  ...TH,
  position: "sticky",
  top: 0,
  zIndex: 20,
  background: "#fff",
};

const TD_FASCIA = {
  padding: "16px 0 8px 0",
  position: "sticky",
  top: 30,
  zIndex: 10,
  background: "#fff",
};

const TD_TOT = {
  borderTop: "1px solid #CBD5E1",
  borderBottom: "none",
  fontWeight: 600,
  color: "#1E293B",
  background: "#F8FAFC",
};

function testoPop(mercati, partite, celle, vuote, perc) {
  return `Le ${numero(partite)} partite hanno ${numero(mercati)} quote ciascuna, `
    + `in tutto ${numero(celle)}. Ne mancano ${numero(vuote)}: ${percentoVuote(perc, vuote)}.`;
}

function percento(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2).replace(".", ",")}%`;
}

/* Con poche caselle vuote su tante partite l'arrotondamento a due decimali
   darebbe 0,00%, indistinguibile da "nessuna casella vuota". */
function percentoVuote(v, vuote) {
  if (v === null || v === undefined) return "—";
  if (Number(v) === 0 && Number(vuote) > 0) return "<0,01%";
  return percento(v);
}

function Pop({ testo, children }) {
  const [aperto, setAperto] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        onClick={() => setAperto(!aperto)}
        style={{
          cursor: "pointer",
          borderBottom: "1px dotted #94A3B8",
        }}
      >
        {children}
      </span>
      {aperto && (
        <span style={POP} onClick={() => setAperto(false)}>{testo}</span>
      )}
    </span>
  );
}

function CoperturaQuote() {
  const [vista, setVista] = useState("mercato");
  const [aiuto, setAiuto] = useState(false);
  const [stagioni, setStagioni] = useState([]);
  const [leghe, setLeghe] = useState([]);

  const [fM, setFM] = useState({ stagioni: [], dal: "", al: "", lega: "" });
  const [fC, setFC] = useState({ stagioni: [], dal: "", al: "" });

  const [datiM, setDatiM] = useState(null);
  const [datiC, setDatiC] = useState(null);
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    fetch(`${API}/stagioni`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setStagioni([...d].sort().reverse()); })
      .catch(() => {});
    fetch(`${API}/copertura-quote/leghe`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setLeghe(d); })
      .catch(() => {});
  }, []);

  const carica = useCallback(() => {
    const f = vista === "mercato" ? fM : fC;
    const q = [`vista=${vista}`];
    if (f.stagioni.length) q.push(`stagioni=${encodeURIComponent(f.stagioni.join(","))}`);
    if (f.dal) q.push(`data_da=${f.dal}`);
    if (f.al) q.push(`data_a=${f.al}`);
    if (vista === "mercato" && f.lega) q.push(`lega=${encodeURIComponent(f.lega)}`);
    setCaricando(true);
    setErrore("");
    fetch(`${API}/copertura-quote?${q.join("&")}`)
      .then((r) => r.json())
      .then((d) => {
        if (d && d.detail) { setErrore(String(d.detail)); return; }
        if (vista === "mercato") setDatiM(d); else setDatiC(d);
      })
      .catch((e) => setErrore(String(e && e.message ? e.message : e)))
      .finally(() => setCaricando(false));
  }, [vista, fM, fC]);

  useEffect(() => { carica(); }, [carica]);

  const f = vista === "mercato" ? fM : fC;
  const setF = vista === "mercato" ? setFM : setFC;

  function toggleStagione(s) {
    setF((v) => {
      const dentro = v.stagioni.includes(s);
      return { ...v, stagioni: dentro ? v.stagioni.filter((x) => x !== s) : [...v.stagioni, s], dal: "", al: "" };
    });
  }

  function azzera() {
    if (vista === "mercato") setFM({ stagioni: [], dal: "", al: "", lega: "" });
    else setFC({ stagioni: [], dal: "", al: "" });
  }

  const tutte = f.stagioni.length === 0;

  return (
    <div style={{ ...CARD, maxWidth: 900 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div style={{ ...H2, marginBottom: 0 }}>Copertura storica delle quote</div>
        <span
          onClick={() => setAiuto(!aiuto)}
          title="Come si legge"
          style={{
            cursor: "pointer", width: 17, height: 17, borderRadius: "50%",
            border: "1px solid #94A3B8", color: "#64748B", fontSize: 11,
            lineHeight: "15px", textAlign: "center", userSelect: "none",
          }}
        >?</span>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button style={vista === "mercato" ? TAB_BTN_ON : TAB_BTN} onClick={() => setVista("mercato")}>
          Per mercato
        </button>
        <button style={vista === "campionato" ? TAB_BTN_ON : TAB_BTN} onClick={() => setVista("campionato")}>
          Per campionato
        </button>
      </div>

      {aiuto && (
        <div style={NOTA}>
          Le quote non entrano tutte nello stesso momento. Le <strong style={{ fontWeight: 600 }}>ore 9</strong> arrivano
          quando nasce il ciclo, per tutte le sue giornate. Le <strong style={{ fontWeight: 600 }}>ore 8</strong> arrivano
          ogni mattina, per le partite del giorno. Le <strong style={{ fontWeight: 600 }}>5 min KO</strong> arrivano la
          mattina dopo la partita.
          <br /><br />
          Il conteggio si ferma dove il dato è dovuto: una quota non ancora arrivata non viene contata come mancante. Per
          questo il numero di partite può essere più basso su alcuni blocchi, e sotto trovi la data fino a cui è arrivato
          il conteggio.
          <br /><br />
          Una quota pari o inferiore a 1,01 non viene registrata e conta come mancante.
        </div>
      )}

      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
        padding: "14px 0", borderBottom: "1px solid #E2E8F0", marginBottom: 4,
      }}>
        <span style={{ fontSize: 12, color: "#64748B" }}>Stagioni</span>
        <select
          style={SELECT_STAG}
          value=""
          onChange={(e) => { if (e.target.value) toggleStagione(e.target.value); }}
        >
          <option value="">{tutte ? "— Tutte le stagioni —" : "— Aggiungi —"}</option>
          {stagioni.filter((s) => !f.stagioni.includes(s)).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {f.stagioni.length > 0 && (
          <span>
            {f.stagioni.map((s) => (
              <span key={s} style={CHIP}>
                {s}
                <span style={CHIP_X} onClick={() => toggleStagione(s)}>{"\u00d7"}</span>
              </span>
            ))}
          </span>
        )}
        <span style={{ width: "100%", height: 0 }} />
        <span style={{ fontSize: 12, color: "#64748B" }}>oppure dal</span>
        <input
          type="date" style={INPUT_DATA} value={f.dal}
          onChange={(e) => setF((v) => ({ ...v, dal: e.target.value, stagioni: [] }))}
        />
        <span style={{ fontSize: 12, color: "#64748B" }}>al</span>
        <input
          type="date" style={INPUT_DATA} value={f.al}
          onChange={(e) => setF((v) => ({ ...v, al: e.target.value, stagioni: [] }))}
        />
        {vista === "mercato" && (
          <>
            <span style={{ fontSize: 12, color: "#64748B", marginLeft: 6 }}>Campionato</span>
            <select
              style={{ ...INPUT_DATA, width: 200 }} value={fM.lega}
              onChange={(e) => setFM((v) => ({ ...v, lega: e.target.value }))}
            >
              <option value="">Tutti</option>
              {leghe.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </>
        )}
        <button style={BTN} onClick={azzera}>Azzera</button>
      </div>

      {errore && (
        <p style={{ ...P, color: "#B91C1C", marginTop: 12 }}>
          Non riesco a caricare i dati: {errore}
        </p>
      )}

      {caricando && !errore && (
        <p style={{ ...P, marginTop: 12, color: "#94A3B8" }}>Calcolo in corso…</p>
      )}

      {!errore && !caricando && vista === "mercato" && datiM && datiM.gruppi && (
        <table style={{ ...TAB, tableLayout: "fixed", marginTop: 4 }}>
          <colgroup>
            <col style={{ width: "34%" }} />
            <col style={{ width: "24%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "22%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={TH_FISSO}>Mercato</th>
              <th style={{ ...TH_FISSO, textAlign: "right" }}>Partite</th>
              <th style={{ ...TH_FISSO, textAlign: "right" }}>Senza quota</th>
              <th style={{ ...TH_FISSO, textAlign: "right" }}>% sul totale</th>
            </tr>
          </thead>
          <tbody>
            {datiM.gruppi.map((g) => {
              const nMercati = g.mercati || g.righe.length;
              const vuoteTot = g.righe.reduce((a, r) => a + (r.senza_quota || 0), 0);
              const celleTot = g.righe.reduce((a, r) => a + (r.partite || 0), 0);
              const percTot = celleTot ? Math.round((10000 * vuoteTot) / celleTot) / 100 : null;
              return (
              <Fragment key={g.id}>
                <tr>
                  <td colSpan={4} style={TD_FASCIA}>
                    <div style={{
                      background: "#F8FAFC", borderRadius: 6, padding: "9px 12px",
                      display: "flex", alignItems: "baseline", justifyContent: "space-between",
                      gap: 12, flexWrap: "wrap",
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>{g.nome}</span>
                      <span style={{ fontSize: 13, color: "#334155" }}>
                        storico dal <strong style={{ fontWeight: 600 }}>{dataEstesa(g.dal)}</strong>
                      </span>
                    </div>
                  </td>
                </tr>
                {g.righe.map((r) => (
                  <tr key={`${g.id}-${r.colonna}`}>
                    <td style={{ ...TD, paddingLeft: 20 }}>{r.mercato}</td>
                    <td style={{ ...TD, textAlign: "right", color: "#64748B" }}>
                      {numero(r.partite)}
                      {r.fino_al && (
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>fino al {dataBreve(r.fino_al)}</div>
                      )}
                    </td>
                    <td style={{ ...TD, textAlign: "right" }}>{numero(r.senza_quota)}</td>
                    <td style={{ ...TD, textAlign: "right", color: r.senza_quota ? "#334155" : "#94A3B8" }}>
                      {percentoVuote(r.perc, r.senza_quota)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...TD, ...TD_TOT, paddingLeft: 20 }}>
                    Totale{" "}
                    <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 11 }}>
                      {nMercati} mercati
                    </span>
                  </td>
                  <td style={{ ...TD, ...TD_TOT }} />
                  <td style={{ ...TD, ...TD_TOT, textAlign: "right" }}>{numero(vuoteTot)}</td>
                  <td style={{ ...TD, ...TD_TOT, textAlign: "right" }}>
                    <Pop testo={testoPop(nMercati, g.partite, celleTot, vuoteTot, percTot)}>
                      {percentoVuote(percTot, vuoteTot)}
                    </Pop>
                  </td>
                </tr>
              </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {!errore && !caricando && vista === "campionato" && datiC && datiC.campionati && (
        <>
          <table style={{ ...TAB, tableLayout: "fixed", marginTop: 12 }}>
            <colgroup>
              <col style={{ width: "40%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...TH_FISSO, border: "none", padding: "6px 8px 0 8px" }} />
                <th
                  colSpan={3}
                  style={{
                    ...TH_FISSO,
                    border: "none",
                    padding: "6px 8px 0 8px",
                  }}
                >
                  <div style={{ marginLeft: "33.333%", textAlign: "center" }}>
                    Quote mancanti
                  </div>
                </th>
              </tr>
              <tr>
                <th style={{ ...TH_FISSO, top: 22 }}>Campionato</th>
                <th style={{ ...TH_FISSO, top: 22, textAlign: "right" }}>Ore 9</th>
                <th style={{ ...TH_FISSO, top: 22, textAlign: "right" }}>5 min KO</th>
                <th style={{ ...TH_FISSO, top: 22, textAlign: "right" }}>Ore 8</th>
              </tr>
            </thead>
            <tbody>
              {datiC.campionati.map((c) => (
                <tr key={c.lega}>
                  <td style={TD}>{c.lega}</td>
                  {["ore9", "5mko", "ore8"].map((k) => {
                    const b = c.blocchi[k];
                    const nMerc = b.mercati || 0;
                    const celle = (b.partite || 0) * nMerc;
                    return (
                      <td key={k} style={{ ...TD, textAlign: "right" }}>
                        {b.perc === null || b.perc === undefined ? "—" : (
                          <Pop testo={testoPop(nMerc, b.partite, celle, b.senza_quota, b.perc)}>
                            {percentoVuote(b.perc, b.senza_quota)}
                          </Pop>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {datiC.campionati.some((c) =>
            ["ore9", "5mko", "ore8"].some((k) =>
              c.blocchi[k].perc === null || c.blocchi[k].perc === undefined)) && (
            <p style={{ ...P, fontSize: 12, color: "#94A3B8", marginTop: 12, marginBottom: 0 }}>
              Il trattino significa che in questo periodo non c'è nessuna partita da contare.
            </p>
          )}
        </>
      )}
    </div>
  );
}
